<script setup lang="ts">
import {
  storeToRefs,
} from "pinia";
import {
  computed, ref, watch,
} from "vue";
import {
  ChevronDown, CircleDollarSign, Clock3, LoaderCircle, WalletCards,
} from "@lucide/vue";
import {
  useSessionStore,
} from "~/stores/session";
import {
  usePersonalCreditsStore,
} from "~/stores/personal-credits";
import {
  buyCreditsFlow,
} from "~/services/buy-credits-dialog/helper";
import {
  HEADER_MENU_POPPER_OPTIONS, headerMenuWidth,
} from "~/utils/header-menu";

const sessionStore = useSessionStore();
const personalCreditsStore = usePersonalCreditsStore();
const {balance: personalCredits} = storeToRefs(personalCreditsStore);
const menuVisible = ref(false);
const purchaseLoading = ref(false);
const hasPendingPayment = computed(() => Boolean(
  sessionStore.registrationPaymentStatus?.pendingOrder,
));

const openCreditsUsage = async () => {
  menuVisible.value = false;
  await navigateTo("/my-credits-usage");
};

const buyPersonalCredits = async () => {
  if (purchaseLoading.value) {
    return;
  }
  menuVisible.value = false;
  purchaseLoading.value = true;
  try {
    await buyCreditsFlow(async () => {
      await personalCreditsStore.refresh();
    }, {
      showPaymentVerification: true,
    });
  } finally {
    purchaseLoading.value = false;
  }
};

watch(
  () => sessionStore.user?.sub,
  (newSub) => {
    if (newSub) {
      void personalCreditsStore.load(newSub);
      void sessionStore.fetchRegistrationPaymentStatus();
    } else {
      personalCreditsStore.clear();
    }
  },
  {immediate: true},
);
</script>

<template>
  <el-popover
    v-model:visible="menuVisible"
    placement="bottom-end"
    :width="headerMenuWidth(280)"
    :popper-options="HEADER_MENU_POPPER_OPTIONS"
    trigger="click"
  >
    <template #reference>
      <button
        type="button"
        class="flex items-center gap-x-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-amber-600 transition-colors hover:bg-amber-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 dark:text-amber-300 dark:hover:bg-amber-950/40"
        :aria-label="$t('NAV.PERSONAL_CREDITS_MENU')"
        :aria-expanded="menuVisible"
      >
        <img
          src="/coin.png"
          class="coin-attention-animation h-5 w-5 object-contain"
          alt=""
        >
        <span>{{ personalCredits }}</span>
        <LoaderCircle
          v-if="purchaseLoading"
          class="h-3.5 w-3.5 animate-spin"
        />
        <ChevronDown
          v-else
          class="h-3.5 w-3.5 transition-transform"
          :class="{ 'rotate-180': menuVisible }"
        />
      </button>
    </template>

    <div class="space-y-1 p-1">
      <button
        v-if="hasPendingPayment"
        type="button"
        class="flex w-full items-center gap-3 rounded-lg bg-amber-50 px-3 py-2.5 text-left text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/70"
        @click="openCreditsUsage"
      >
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
          <Clock3 class="h-4 w-4" />
        </span>
        {{ $t('NAV.PAY_PENDING_ORDER') }}
      </button>

      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:text-slate-200 dark:hover:bg-slate-800"
        :disabled="purchaseLoading"
        @click="buyPersonalCredits"
      >
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
          <WalletCards class="h-4 w-4" />
        </span>
        {{ $t('NAV.BUY_PERSONAL_CREDITS') }}
      </button>

      <button
        type="button"
        class="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
        @click="openCreditsUsage"
      >
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
          <CircleDollarSign class="h-4 w-4" />
        </span>
        {{ $t('NAV.MY_CREDITS_USAGE') }}
      </button>
    </div>
  </el-popover>
</template>
